// src/section/CMS/components/customPage/FeatureBadgesRenderer.tsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTypography } from '../../../../context/TypographyContext';
import { getFileServerBase } from './fileServerBase';

const FILE_SERVER_BASE = `${getFileServerBase()}/media/large/`;

const resolveMediaUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${FILE_SERVER_BASE}${url}`;
};

// ─── TYPES ───────────────────────────────────────────────────────

// A suitability symbol as returned by the CMS page response (symbols.web_suitability).
interface SuitabilitySymbol {
  symbol_name: string;
  file_name?: string;
  file_url?: string;
  weight?: number;
}

// Manual copy authored in the hub Feature Badges block (badge_items).
interface BadgeDescription {
  id?: string;
  symbol_name?: string;
  description?: string;
}

interface FeatureBadgesRendererProps {
  // The page's `symbols` object (top-level on the CMS response). Badges are all
  // the page's symbols across these groups (not just web_suitability).
  symbols: Record<string, any> | null;
  // Manual descriptions authored in the hub (badge_items).
  descriptions?: BadgeDescription[];
  // Link text for the modal trigger (falls back to "Learn more about badges").
  learnMoreText?: string;
  // Optional max width for the component (CSS value, e.g. "800px"); centered when set.
  maxWidth?: string;
  showLearnMore?: boolean;
  modalTitle?: string;
}

// Symbol groups in display order; badges are flattened across all of them.
const SYMBOL_GROUPS = ['web_suitability', 'symbols', 'standards', 'cleaning_instructions'];

const flattenSymbols = (symbols: Record<string, any> | null | undefined): SuitabilitySymbol[] => {
  if (!symbols) return [];
  const seen = new Set<string>();
  const out: SuitabilitySymbol[] = [];
  SYMBOL_GROUPS.forEach((group) => {
    const arr = symbols[group];
    if (Array.isArray(arr)) {
      arr.forEach((s: SuitabilitySymbol) => {
        if (s?.symbol_name && !seen.has(s.symbol_name)) {
          seen.add(s.symbol_name);
          out.push(s);
        }
      });
    }
  });
  return out;
};

const badgeImage = (b: SuitabilitySymbol): string =>
  resolveMediaUrl(b.file_url || b.file_name);

// ─── MAIN RENDERER ───────────────────────────────────────────────

const FeatureBadgesRenderer: React.FC<FeatureBadgesRendererProps> = ({
                                                                       symbols,
                                                                       descriptions = [],
                                                                       learnMoreText,
                                                                       maxWidth,
                                                                       showLearnMore = true,
                                                                       modalTitle,
                                                                     }) => {
  const { styleFor } = useTypography();
  const [open, setOpen] = useState(false);

  // Badges in the order authored in the hub. badge_items (descriptions) carries
  // the drag-and-drop order; symbols are shown in that order, and any symbol not
  // present in badge_items keeps its natural order, appended at the end.
  const badges = useMemo(() => {
    const flat = flattenSymbols(symbols);
    return [...flat].sort((a, b) => {
      const wa = typeof a.weight === 'number' ? a.weight : 500;
      const wb = typeof b.weight === 'number' ? b.weight : 500;
      if (wa !== wb) return wa - wb;
      // Stable tie-breaker: original flatten order
      return flat.indexOf(a) - flat.indexOf(b);
    });
  }, [symbols]);

  // Match a manually-written description to a badge by symbol_name.
  const descByName = useMemo(() => {
    const map = new Map<string, string>();
    descriptions.forEach((d) => {
      if (d.symbol_name && d.description) map.set(d.symbol_name, d.description);
    });
    return map;
  }, [descriptions]);

  const descriptionFor = (badge: SuitabilitySymbol): string =>
    descByName.get(badge.symbol_name) ?? '';

  if (!Array.isArray(badges) || badges.length === 0) return null;

  const linkText = learnMoreText?.trim() || 'Learn more about badges';

  return (
    <Container maxWidth="lg" disableGutters sx={maxWidth ? { maxWidth: `${maxWidth} !important` } : undefined}>
      {/* ── Badge strip (all badges in one line) ─────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: { xs: 2, md: 2.5 },
        }}
      >
        {badges.map((badge) => {
          const src = badgeImage(badge);
          if (!src) return null;
          return (
            <Box
              key={badge.symbol_name}
              component="img"
              src={src}
              alt={badge.symbol_name}
              sx={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
          );
        })}
      </Box>

      {showLearnMore && (
        <>
          {/* ── Learn more trigger ───────────────────────────────────── */}
          <Link
            component="button"
            type="button"
            onClick={() => setOpen(true)}
            sx={{
              mt: 2.5,
              display: 'inline-block',
              ...styleFor('P1'),
              fontWeight: 700,
              color: 'text.primary',
              textDecoration: 'underline',
              textDecorationColor: 'currentColor',
              cursor: 'pointer',
            }}
          >
            {linkText}
          </Link>

          {/* ── Modal (matches the global search multi-filter modal) ──── */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            maxWidth={false}
            fullWidth
            PaperProps={{
              sx: {
                width: '90vw',
                height: '90vh',
                maxWidth: '90vw',
                maxHeight: '90vh',
                margin: '5vh',
                borderRadius: '12px',
                overflow: 'hidden',
              },
            }}
          >
            {/* Header with close button */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 5,
                pt: 3,
                pb: 0,
              }}
            >
              <Typography sx={{ ...styleFor('H2'), color: '#333' }}>
                {modalTitle?.trim() || 'Performance badges'}
              </Typography>
              <IconButton
                onClick={() => setOpen(false)}
                aria-label="Close"
                sx={{ color: '#666', '&:hover': { backgroundColor: '#e0e0e0' } }}
              >
                <Close sx={{ fontSize: 32 }} />
              </IconButton>
            </Box>

            <DialogContent sx={{ flex: 1, overflow: 'auto', px: 5, pt: 0, pb: 5 }}>
              {badges.map((badge, index) => {
                const src = badgeImage(badge);
                const copy = descriptionFor(badge);
                return (
                  <Box key={badge.symbol_name} sx={{ pt: index === 0 ? '30px' : '45px' }}>
                    {src && (
                      <Box
                        component="img"
                        src={src}
                        alt={badge.symbol_name}
                        sx={{ width: 72, height: 72, objectFit: 'contain', borderRadius: '50%', display: 'block', mb: '20px' }}
                      />
                    )}
                    <Typography
                      sx={{ ...styleFor('P1'), fontSize: '18px', fontWeight: 700, textTransform: 'uppercase', mb: 1 }}
                    >
                      {badge.symbol_name}
                    </Typography>
                    {copy && (
                      <Typography sx={{ ...styleFor('P1'), fontSize: '18px' }}>
                        {copy}
                      </Typography>
                    )}
                    <Divider sx={{ mt: '45px', borderColor: 'rgba(0,0,0,0.4)' }} />
                  </Box>
                );
              })}
            </DialogContent>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default FeatureBadgesRenderer;
